import { alpha, createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { NAV_ITEM_HEIGHT, PALETTE, RADIUS, SHADOWS } from './tokens';
import { onColour, readableRoleText, tint, type Palette, type RoleColours } from './colourRoles';

/**
 * How the vendor is configured to look like the design system.
 *
 * This is the densest vendor coupling in the application — every key below is
 * one of their component slot names — which is exactly why it lives inside
 * `src/ui` rather than above it. The design tokens it consumes are in
 * `./tokens`, are ours, and are what a replacement would be configured from.
 */

// ── TypeScript augmentation so `color="neutral"` is accepted on Button/IconButton ──
declare module '@mui/material/styles' {
  interface Palette     { neutral: Palette['primary']; gradient: HeadingGradient; navigation: NavigationColours; grid: GridColours; }
  interface PaletteOptions { neutral?: PaletteOptions['primary']; gradient?: HeadingGradient; navigation?: NavigationColours; grid?: GridColours; }
}

interface HeadingGradient { dark: string; blue: string; tealDeep: string; }
/** The bar under the navbar: the tenant's dark primary, its amber as the selected tab's mark. */
interface NavigationColours { main: string; accent: string; contrastText: string; }
/**
 * The management grid: the toolbar's wash, the three tints mixed from it with white, its controls' fill,
 * a chip per role (a wash of the role, its text the role's darkest shade that reads on it) and a failed read's title.
 */
interface GridColours {
  toolbar: string; header: string; footer: string; hover: string; control: string;
  chip: Record<GridChipTone, { background: string; text: string }>;
  failedText: string;
}
type GridChipTone = 'primary' | 'success' | 'warning' | 'error' | 'info';
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides { neutral: true; }
}
declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides { neutral: true; }
}

const CHIP_WASH = 0.14;

const chipOf = (role: RoleColours) => {
  const background = tint(role.main, CHIP_WASH);
  return { background, text: readableRoleText(role, background) };
};

export function buildTheme(palette: Palette) {
  return createTheme({
    palette: {
      primary:    palette.primary,
      secondary:  palette.secondary,
      neutral:    palette.neutral,
      success:    palette.success,
      error:      palette.error,
      warning:    palette.warning,
      info:       palette.info,
      background: palette.background,
      text:       palette.text,
      divider:    palette.divider,
      gradient:   palette.gradient,
      navigation: {
        main: palette.primary.dark,
        accent: palette.button.secondary.main,
        contrastText: onColour(palette.primary.dark),
      },
      grid: {
        toolbar: palette.surface.toolbar,
        header: tint(palette.surface.toolbar, 0.55),
        footer: tint(palette.surface.toolbar, 0.4),
        hover: tint(palette.surface.toolbar, 0.28),
        control: palette.background.paper,
        chip: {
          primary: chipOf(palette.primary),
          success: chipOf(palette.success),
          warning: chipOf(palette.warning),
          error: chipOf(palette.error),
          info: chipOf(palette.info),
        },
        failedText: readableRoleText(palette.error, palette.background.paper),
      },
      // MUI X DataGrid v7+ palette hook — headers get the sunken surface.
      DataGrid: {
        bg: palette.background.paper,
        headerBg: palette.surface.header,
        pinnedBg: palette.background.paper,
      },
    },

    typography: {
      fontFamily: '"Noto Sans", "Manrope", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 800, letterSpacing: '-0.6px' },
      h5: { fontWeight: 800, letterSpacing: '-0.4px' },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600, color: palette.text.secondary },
      body2: { color: palette.text.secondary },
      caption: { color: palette.text.secondary, fontSize: '0.75rem', letterSpacing: '0.3px' },
      overline: { fontWeight: 700, letterSpacing: '1.2px', color: palette.text.secondary },
    },

    shape: {
      borderRadius: RADIUS.md,
    },

    components: {
      /* ── Cards ──────────────────────────────── */
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md,
            boxShadow: 'none',
            border: `1px solid ${palette.divider}`,
            backgroundColor: palette.background.paper,
            color: palette.text.primary,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 24,
            '&:last-child': { paddingBottom: 24 },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          subheader: {
            color: palette.text.secondary,
          },
        },
      },

      /* ── Links ───────────────────────────────── */
      MuiLink: {
        styleOverrides: {
          root: { color: palette.link, fontWeight: 600 },
        },
      },

      /* ── Paper ───────────────────────────────── */
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          elevation1: { boxShadow: SHADOWS.surface },
        },
      },

      /* ── Buttons ─────────────────────────────── */
      MuiButton: {
        defaultProps: { color: 'primary' },
        styleOverrides: {
          root: {
            borderRadius: RADIUS.sm,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.875rem',
            padding: '9px 20px',
            letterSpacing: 0.2,
            '&.Mui-disabled': {
              color: palette.text.disabled,
            },
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: `0 4px 12px ${alpha(palette.primary.dark, 0.3)}` },
            '&.Mui-disabled': {
              color: alpha(palette.text.primary, 0.45),
              backgroundColor: alpha(palette.text.primary, 0.08),
            },
          },
          outlined: {
            backgroundColor: palette.background.paper,
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5 },
          },
        },
        // Per-variant-and-colour styles moved out of styleOverrides upstream; the
        // values are unchanged, only where they are declared.
        variants: [
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              backgroundColor: palette.button.primary.main,
              color: palette.button.primary.contrastText,
              '&:hover': { backgroundColor: palette.primary.dark },
            },
          },
          {
            props: { variant: 'contained', color: 'secondary' },
            style: {
              backgroundColor: palette.button.secondary.main,
              color: palette.button.secondary.contrastText,
              '&:hover': { backgroundColor: alpha(palette.button.secondary.main, 0.88) },
            },
          },
          {
            props: { variant: 'text', color: 'primary' },
            style: { color: palette.primary.main },
          },
        ],
      },

      /* ── Icon buttons ────────────────────────── */
      MuiIconButton: {
        defaultProps: { color: 'primary' },
        styleOverrides: {
          root: {
            transition: 'background-color 0.15s ease, color 0.15s ease',
            '&.MuiIconButton-colorPrimary:hover':   { backgroundColor: alpha(palette.primary.main, 0.10) },
            '&.MuiIconButton-colorSecondary:hover': { backgroundColor: alpha(palette.secondary.main, 0.10) },
            '&.MuiIconButton-colorNeutral:hover':   { backgroundColor: alpha(palette.neutral.main, 0.10) },
            '&.Mui-disabled': { color: palette.text.disabled },
          },
        },
      },

      /* ── Text fields ─────────────────────────── */
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.paper,
            borderRadius: RADIUS.sm,
            '& .MuiOutlinedInput-notchedOutline':             { borderColor: 'rgba(32,60,72,0.2)', borderWidth: 1.5 },
            '&:hover .MuiOutlinedInput-notchedOutline':       { borderColor: palette.primary.main },
            '&.Mui-focused':                                  { boxShadow: `0 0 0 3px ${alpha(palette.primary.main, 0.18)}` },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary.main, borderWidth: 1.5 },
          },
        },
      },
      /* ── Date fields ─────────────────────────── */
      // The pickers stopped rendering their field through MuiOutlinedInput and
      // now use a slot of their own, so the input treatment has to be said twice.
      // Without this every date field reverts to the vendor's default look: the
      // global radius instead of the control radius, a transparent background
      // instead of paper, and no focus ring.
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.paper,
            borderRadius: RADIUS.sm,
            '& .MuiPickersOutlinedInput-notchedOutline':       { borderColor: 'rgba(32,60,72,0.2)', borderWidth: 1.5 },
            '&:hover .MuiPickersOutlinedInput-notchedOutline': { borderColor: palette.primary.main },
            '&.Mui-focused':                                   { boxShadow: `0 0 0 3px ${alpha(palette.primary.main, 0.18)}` },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': { borderColor: palette.primary.main, borderWidth: 1.5 },
          },
        },
      },
      MuiPickersInputBase: {
        styleOverrides: {
          root: { fontSize: '0.875rem' },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            color: palette.text.secondary,
            '&.Mui-focused': { color: palette.primary.main },
          },
        },
      },

      /* ── Drawer ───────────────────────────────── */
      MuiDrawer: {
        styleOverrides: { paper: { borderRight: 'none' } },
      },

      /* ── List items ──────────────────────────── */
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.xs,
            transition: 'background-color 0.15s ease, color 0.15s ease',
          },
        },
      },

      /* ── Tabs (secondary navigation) ─────────── */
      MuiTabs: {
        // Scrollable everywhere: the member shell's mobile line is `md` and the
        // admin shell's is `sm`, so a strip that reads a breakpoint is wrong in
        // one of them. Overflow scrolls inside the strip rather than the page.
        defaultProps: { variant: 'scrollable', scrollButtons: 'auto' },
        styleOverrides: {
          root: {
            minHeight: NAV_ITEM_HEIGHT,
            borderBottom: `1px solid ${palette.divider}`,
          },
          indicator: {
            height: 3,
            backgroundColor: palette.primary.main,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            // Matches the sidebar row, which tests/layout.spec.ts pins at 44px:
            // a tab and a nav row are the same kind of target one level apart.
            minHeight: NAV_ITEM_HEIGHT,
            minWidth: 0,
            borderRadius: `${RADIUS.xs}px ${RADIUS.xs}px 0 0`,
            padding: '9px 16px',
            textTransform: 'none',
            fontSize: '0.875rem',
            // Constant across states: the sidebar's 400→700 jump reflows the row
            // it sits in, which on a horizontal strip would shift every label.
            fontWeight: 600,
            letterSpacing: 0.2,
            color: palette.text.secondary,
            transition: 'background-color 0.15s ease, color 0.15s ease',
            '&:hover': { backgroundColor: alpha(palette.primary.main, 0.06) },
            '&.Mui-selected': {
              color: palette.primary.main,
              backgroundColor: alpha(palette.primary.main, 0.1),
            },
            '&.Mui-disabled': { color: palette.text.disabled },
          },
        },
      },

      /* ── Form controls ───────────────────────── */
      MuiCheckbox:    { defaultProps: { color: 'primary' } },
      MuiRadio:       { defaultProps: { color: 'primary' } },
      MuiSwitch:      { defaultProps: { color: 'primary' } },
      MuiSelect: {
        styleOverrides: { icon: { color: palette.text.secondary } },
      },
      MuiNativeSelect: {
        styleOverrides: { icon: { color: palette.text.secondary } },
      },

      /* ── Chip ────────────────────────────────── */
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: RADIUS.xs, fontWeight: 600 },
        },
      },

      /* ── Tooltip ─────────────────────────────── */
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: palette.text.primary,
            fontSize: '0.75rem',
            borderRadius: RADIUS.xs,
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 700 },
        },
      },

      /* ── DataGrid ─────────────────────────────── */
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 'none',
            fontFamily: '"Noto Sans", "Manrope", "Inter", "Roboto", sans-serif',
            '& .MuiDataGrid-columnSeparator': { display: 'none' },
          },
          columnHeaderTitle: {
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            color: palette.primary.dark,
          },
          row:  { '&:hover': { backgroundColor: palette.background.default } },
          cell: {
            borderBottom: `1px solid ${alpha(palette.text.primary, 0.08)}`,
            fontSize: '0.875rem',
            fontVariantNumeric: 'tabular-nums',
          },
          footerContainer: {
            borderTop: `1px solid ${palette.divider}`,
            backgroundColor: palette.surface.header,
          },
        },
      },
    },
  });
}

export const theme = buildTheme(PALETTE);
